import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit {
  chatData: any;
  analysisData: any;
  loading: boolean;
  currentLang: string;

  constructor( 
    private apisService: ApisService,
    private router: Router,
    public _translateService: TranslateService
  ) {
    this.currentLang = this._translateService.currentLang || 'ar';
  } 

  ngOnInit(): void {
    this.getData();
    this.getAnalysis();
    
    // الاستماع لتغيير اللغة
    this._translateService.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
    });
  }

  getData() {
    this.apisService.getchatData().subscribe({
      next: (res) => {
        this.chatData = res.chats || [];
        console.log(this.chatData, 'chatData');
      },
      error: (error) => {
        console.error('Error fetching chatData:', error);
      }
    });
  }

  getAnalysis() {
    this.loading = true;
    this.apisService.getChatAnalysis().subscribe({
      next: (res) => {
        this.analysisData = res.data || [];
        console.log(this.analysisData, 'analysisData');
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching analysisData:', error);
        this.loading = false;
      }
    });
  }

  // دالة لتنسيق الأرقام الكبيرة
  formatNumber(num: number): string {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  getChangePercentage(count: number, type: string): { value: string, class: string } {
    const changes: any = {
      chats_count: { value: '+12% هذا الشهر', class: 'up' },
      messages_count: { value: '+8% هذا الأسبوع', class: 'up' },
      employees_count: { value: 'مستقر', class: 'neutral' },
      investors_count: { value: '+5% هذا الشهر', class: 'up' }
    };
    
    return changes[type] || { value: '--', class: 'neutral' };
  }

  formatTime(dateString: string): string {
    if (!dateString) return '--:--';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--:--';
    
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: this.currentLang === 'ar'
    };
    
    return date.toLocaleTimeString(this.currentLang === 'ar' ? 'ar-SA' : 'en-US', options);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '--/--/----';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--/--/----';
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return date.toLocaleDateString(this.currentLang === 'ar' ? 'ar-SA' : 'en-US', options);
  }

  sendChatId(chat_id: any) {
    this.router.navigate(['/chats-detailes', chat_id]);
  }

  formatRelativeTime(dateString: string): string {
    if (!dateString) return this.currentLang === 'ar' ? 'تاريخ غير معروف' : 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (isNaN(date.getTime())) {
      return this.currentLang === 'ar' ? 'تاريخ غير صالح' : 'Invalid date';
    }

    // الترجمات
    const translations = {
      ar: {
        now: 'الآن',
        minute: 'دقيقة',
        minutes: 'دقيقة',
        hour: 'ساعة',
        hours: 'ساعة',
        day: 'يوم',
        days: 'يوم',
        yesterday: 'أمس',
        before: 'قبل'
      },
      en: {
        now: 'Now',
        minute: 'minute',
        minutes: 'minutes',
        hour: 'hour',
        hours: 'hours',
        day: 'day',
        days: 'days',
        yesterday: 'Yesterday',
        before: ''
      }
    };

    const t = translations[this.currentLang as keyof typeof translations];

    if (diffInMinutes < 1) {
      return t.now;
    }

    if (diffInMinutes < 60) {
      if (this.currentLang === 'ar') {
        return `${t.before} ${diffInMinutes} ${t.minute}`;
      } else {
        return `${diffInMinutes} ${diffInMinutes === 1 ? t.minute : t.minutes} ago`;
      }
    }

    if (diffInHours < 24) {
      if (this.currentLang === 'ar') {
        if (diffInHours === 1) return `${t.before} ساعة`;
        return `${t.before} ${diffInHours} ${t.hours}`;
      } else {
        return `${diffInHours} ${diffInHours === 1 ? t.hour : t.hours} ago`;
      }
    }

    if (diffInDays < 7) {
      if (diffInDays === 1) {
        return t.yesterday;
      }
      if (this.currentLang === 'ar') {
        if (diffInDays === 2) return 'قبل يومين';
        return `${t.before} ${diffInDays} ${t.days}`;
      } else {
        return `${diffInDays} ${diffInDays === 1 ? t.day : t.days} ago`;
      }
    }

    // أكثر من أسبوع - نعود لتنسيق التاريخ العادي
    return this.formatDate(dateString);
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return this.currentLang === 'ar' ? 'تاريخ غير معروف' : 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    
    if (isNaN(date.getTime())) {
      return this.currentLang === 'ar' ? 'تاريخ غير صالح' : 'Invalid date';
    }

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();

    const translations = {
      ar: {
        today: 'اليوم',
        yesterday: 'أمس'
      },
      en: {
        today: 'Today',
        yesterday: 'Yesterday'
      }
    };

    const t = translations[this.currentLang as keyof typeof translations];

    if (isToday) {
      return `${t.today} ${this.formatTime(dateString)}`;
    } else if (isYesterday) {
      return `${t.yesterday} ${this.formatTime(dateString)}`;
    } else {
      return `${this.formatDate(dateString)} - ${this.formatTime(dateString)}`;
    }
  }

  // دالة إضافية لتنسيق التاريخ بشكل قصير
  formatShortDate(dateString: string): string {
    if (!dateString) return '--/--/----';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--/--/----';
    
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    return date.toLocaleDateString(this.currentLang === 'ar' ? 'ar-SA' : 'en-US', options);
  }

  // دالة إضافية لتنسيق اليوم والشهر فقط
  formatDayMonth(dateString: string): string {
    if (!dateString) return '--/--';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--/--';
    
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric'
    };
    
    return date.toLocaleDateString(this.currentLang === 'ar' ? 'ar-SA' : 'en-US', options);
  }
}